import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PropertyDto, PropertyRoomType } from '@workspace/data-types';
import { LuxonDatePipe } from '@workspace/ng-aura';
import { EtxeEnumDisplayPipe } from '../../../../pipes/etxe-enum-display.pipe';

@Component({
  selector: 'etxe-property-view-key-criteria',
  imports: [CommonModule, MatIconModule, MatTooltipModule, LuxonDatePipe, EtxeEnumDisplayPipe],
  templateUrl: './property-view-key-criteria.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
  },
})
export class PropertyViewKeyCriteriaComponent {
  property = input.required<PropertyDto>();

  // Eligible living space room types counted as main rooms in real estate (>= 9m²)
  readonly eligibleMainRoomTypes = new Set<PropertyRoomType>([
    PropertyRoomType.LIVING_ROOM,
    PropertyRoomType.DINING_ROOM,
    PropertyRoomType.OFFICE,
    PropertyRoomType.LIBRARY,
    PropertyRoomType.PLAYROOM,
    PropertyRoomType.MUSIC_STUDIO,
  ]);

  // Main rooms count (chambres, salons, séjours, bureaux >= 9m² excluant cuisines, sanitaires, couloirs)
  mainRoomsCount = computed<number | null>(() => {
    const interior = this.property().characteristics?.interior;
    if (!interior) return null;

    let count = 0;

    // Chambres >= 9 m²
    if (Array.isArray(interior.bedroom)) {
      for (const bedroom of interior.bedroom) {
        const surface = Number(bedroom.surface);
        if (Number.isNaN(surface) || surface >= 9) {
          count++;
        }
      }
    }

    // Pièces de vie principales >= 9 m²
    if (Array.isArray(interior.rooms)) {
      for (const room of interior.rooms) {
        const surface = Number(room.surface);
        if (room.type && this.eligibleMainRoomTypes.has(room.type)) {
          if (Number.isNaN(surface) || surface >= 9) {
            count++;
          }
        }
      }
    }

    return count > 0 ? count : null;
  });

  // Surface totals
  totalInteriorSurface = computed<number>(() => {
    const interior = this.property().characteristics?.interior;
    if (!interior) return 0;
    const roomsSum = (interior.rooms || []).reduce((acc, r) => acc + (r.surface || 0), 0);
    const bedroomsSum = (interior.bedroom || []).reduce((acc, b) => acc + (b.surface || 0), 0);
    const bathroomsSum = (interior.bathroom || []).reduce((acc, b) => acc + (b.surface || 0), 0);
    const showerSum = (interior.showerRoom || []).reduce((acc, s) => acc + (s.surface || 0), 0);
    return roomsSum + bedroomsSum + bathroomsSum + showerSum;
  });
}
